import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router';

import { CanvasViewport } from '@/components/app-editor/canvas-viewport';
import { useEditorChrome } from '@/components/app-editor/use-editor-chrome';
import {
  VisualEditorLayout,
  type PublishState,
} from '@/components/app-editor/visual-editor-layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { AdvancedPanel } from '@/components/website-banner/advanced-panel';
import { ColorsPanel } from '@/components/website-banner/colors-panel';
import { ContentPanel } from '@/components/website-banner/content-panel';
import { LivePreviewActions } from '@/components/website-banner/live-preview-actions';
import { PositionPanel } from '@/components/website-banner/position-panel';
import { WebsiteBannerSaveConfirmDialog } from '@/components/website-banner/save-confirm-dialog';
import { SiteBannerCanvas } from '@/components/website-banner/site-banner-canvas';
import { useBlockerFull } from '@/hooks/use-blocker-full';
import { useGetWebsiteBanner } from '@/hooks/use-get-website-banner';
import { useTenant } from '@/hooks/use-tenant';
import { useWebsiteBannerForm } from '@/hooks/use-website-banner-form';
import type { SetPreviewFormSnapshotRef } from '@/hooks/use-website-banner-form';
import { useWebsiteBannerLiveSession } from '@/hooks/use-website-banner-live-session';
import { useWebsiteBannerPreview } from '@/hooks/use-website-banner-preview';
import { useWebsiteBannerSave } from '@/hooks/use-website-banner-save';
import { useI18nContext } from '@/i18n/i18n-react';
import { IPHONE_FRAME_ASPECT } from '@/lib/app-editor/device-fit';
import { bannerDtoToForm } from '@/lib/website-banner-constants';

type TabId = 'content' | 'colors' | 'position' | 'advanced';

export default function WebsiteBannerPage() {
  const { LL } = useI18nContext();
  const tenant = useTenant();
  const navigate = useNavigate();
  const chrome = useEditorChrome<TabId>('content');

  const bannerId = tenant.websiteBannerId ?? null;
  const bannerQuery = useGetWebsiteBanner();
  const bannerData = bannerQuery.data ?? undefined;

  const { saveMutation, shopifySetupComplete } = useWebsiteBannerSave({
    bannerId,
    LL,
  });

  const setPreviewFormSnapshotRef: SetPreviewFormSnapshotRef = useRef(null);

  const formState = useWebsiteBannerForm({
    bannerData: bannerData ?? null,
    bannerId,
    saveMutation,
    setPreviewFormSnapshotRef,
    LL,
  });

  const previewState = useWebsiteBannerPreview({
    form: formState.form,
    bannerId,
    hasBannerData: bannerQuery.data != null,
    setPreviewFormSnapshotRef,
  });

  const liveSessionState = useWebsiteBannerLiveSession({
    bannerId,
    previewFormSnapshot: previewState.previewFormSnapshot,
    LL,
  });

  useBlockerFull(formState.isDirty && !saveMutation.isPending);

  useEffect(() => {
    if (bannerData) {
      setPreviewFormSnapshotRef.current?.(bannerDtoToForm(bannerData));
    }
  }, [bannerData]);

  useEffect(() => {
    if (bannerQuery.error) {
      toast.error(
        bannerQuery.error instanceof Error
          ? bannerQuery.error.message
          : LL.websiteBannerPage.toasts.failedToLoadBanner()
      );
    }
  }, [bannerQuery.error, LL]);

  const isLoadingBanner =
    (bannerId != null && bannerQuery.isLoading) ||
    (!bannerId && shopifySetupComplete);

  if (
    !bannerId &&
    tenant.ecommercePlatform === 'shopify' &&
    !shopifySetupComplete
  ) {
    return <Navigate to="setup" />;
  }

  if (isLoadingBanner) {
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 aria-hidden className="h-10 w-10 animate-spin" />
        <span className="text-sm font-medium">
          {LL.websiteBannerPage.loadingBanner()}
        </span>
      </div>
    );
  }

  const publishState: PublishState = saveMutation.isPending
    ? 'pending'
    : formState.isDirty
      ? 'dirty'
      : saveMutation.isSuccess
        ? 'published'
        : 'clean';

  return (
    <>
      <VisualEditorLayout<TabId>
        title={LL.websiteBannerPage.title()}
        backTo="../"
        chrome={chrome}
        active={{
          checked: formState.formEnabled,
          onCheckedChange: (enabled) =>
            formState.setForm((prev) => ({ ...prev, enabled })),
          label: LL.appEditor.active(),
        }}
        publish={{ state: publishState, onPublish: formState.handleSave }}
        notice={LL.appEditor.unpublishedNoticeWebsite()}
        tabs={[
          {
            id: 'content',
            label: LL.websiteBannerEditor.tabs.content(),
            content: (
              <ContentPanel
                form={formState.form}
                updateContent={formState.updateContent}
              />
            ),
          },
          {
            id: 'colors',
            label: LL.websiteBannerEditor.tabs.colors(),
            content: (
              <ColorsPanel
                form={formState.form}
                updateTheme={formState.updateTheme}
              />
            ),
          },
          {
            id: 'position',
            label: LL.websiteBannerEditor.tabs.position(),
            content: (
              <PositionPanel
                form={formState.form}
                updatePosition={formState.updatePosition}
              />
            ),
          },
          {
            id: 'advanced',
            label: LL.websiteBannerEditor.tabs.advanced(),
            // Not mounted until opened: its CSS editors are Monaco instances.
            content:
              chrome.activeTab === 'advanced' ? (
                <div className="space-y-4">
                  <AdvancedPanel
                    form={formState.form}
                    setForm={formState.setForm}
                    updateCustomStyles={formState.updateCustomStyles}
                  />
                  {tenant.ecommercePlatform === 'shopify' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="max-md:min-h-touch"
                      onClick={() => navigate('setup')}
                    >
                      {LL.websiteBannerPage.footer.activateOnShopifyAgain()}
                    </Button>
                  )}
                </div>
              ) : null,
          },
        ]}
        canvas={
          <CanvasViewport
            frameAspect={IPHONE_FRAME_ASPECT}
            className="bg-muted/20"
            toolbar={
              <LivePreviewActions
                tenantId={tenant.id}
                bannerId={bannerId}
                liveSessionToken={liveSessionState.liveSessionToken}
                livePreviewUrl={liveSessionState.livePreviewUrl}
                liveSessionMutation={liveSessionState.liveSessionMutation}
                forceRefreshMutation={liveSessionState.forceRefreshMutation}
              />
            }
          >
            {({ screenWidth }) => (
              <div className="relative">
                <SiteBannerCanvas
                  previewHtml={previewState.previewHtml}
                  isPreviewLoading={
                    previewState.previewQuery.isFetching &&
                    previewState.previewHtml == null
                  }
                  previewFailed={previewState.previewQuery.isError}
                  form={formState.form}
                  storeDomain={tenant.storeWebsiteDomain ?? ''}
                  screenWidth={screenWidth}
                  onBannerPress={() => chrome.focusTarget('content')}
                />
                {!formState.formEnabled && (
                  <Badge
                    variant="secondary"
                    className="absolute left-1/2 top-2 -translate-x-1/2 shadow"
                  >
                    {LL.websiteBannerEditor.canvas.off()}
                  </Badge>
                )}
              </div>
            )}
          </CanvasViewport>
        }
      />
      <WebsiteBannerSaveConfirmDialog
        open={formState.showSaveConfirm}
        onOpenChange={formState.setShowSaveConfirm}
        title={formState.saveConfirmContent.title}
        description={formState.saveConfirmContent.description}
        onConfirm={formState.handleConfirmSave}
        cancelLabel={LL.websiteBannerPage.saveConfirm.cancel()}
        confirmLabel={LL.websiteBannerPage.saveConfirm.confirm()}
      />
    </>
  );
}
